import { useEffect, useState, useMemo } from "react";
import UserLayout from "@/components/UserLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Plus, Maximize2, Search, Zap, Phone, ChevronRight, Package, TrendingUp } from "lucide-react";
import { ServiceIcon } from "@/components/ServiceIcon";

export default function BuyNumber() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [services, setServices] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [service, setService] = useState("");
  const [search, setSearch] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data));
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  useEffect(() => {
    if (countryId && service) {
      setLoadingTiers(true);
      setTiers([]);
      setSelectedTier(null);
      api.get(`/price-tiers?country_id=${countryId}&service=${service}`)
        .then((r) => {
          const arr = r.data.tiers || [];
          setTiers(arr);
          if (arr.length > 0) setSelectedTier(arr[0]); // pre-select cheapest
        })
        .catch((err) => { toast.error(formatApiError(err)); setTiers([]); })
        .finally(() => setLoadingTiers(false));
    } else {
      setTiers([]);
      setSelectedTier(null);
    }
  }, [countryId, service]);

  const buy = async () => {
    if (!countryId || !service || !selectedTier) return;
    setBuying(true);
    try {
      const { data } = await api.post("/numbers/buy", {
        country_id: countryId,
        service,
        max_price: selectedTier.raw_cost,
        provider: selectedTier.provider,
        tier: selectedTier.tier || null,
      });
      toast.success(`Numara alındı: +${data.number.phone}`);
      await refresh();
      navigate("/numbers");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBuying(false);
    }
  };

  const filteredServices = useMemo(() => {
    if (!search.trim()) return services;
    const q = search.toLowerCase();
    return services.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q));
  }, [services, search]);

  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return countries;
    const q = countrySearch.toLowerCase();
    return countries.filter((c) => c.country_name.toLowerCase().includes(q));
  }, [countries, countrySearch]);

  const selectedCountry = countries.find((c) => c.id === countryId);
  const selectedService = services.find((s) => s.code === service);

  return (
    <UserLayout>
      <div className="mb-6">
        <span className="inline-flex items-center gap-1.5 border border-[hsl(217,91%,60%)]/40 bg-[hsl(217,91%,60%)]/10 text-[hsl(217,91%,68%)] px-3 py-1 rounded-full text-xs font-bold">
          <Zap className="w-3 h-3" /> Hızlı numara merkezi
        </span>
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-black mb-3">Numara satın al</h1>
      <p className="text-white/60 text-sm md:text-base leading-relaxed max-w-2xl mb-8">
        Servisi ve ülkeyi seç; kalite–fiyat kademesini karşılaştır ve tek tıkla satın al.
      </p>

      {/* Balance card */}
      <div className="card-glow rounded-3xl p-6 md:p-7 mb-6" data-testid="balance-card">
        <div className="text-white/80 text-sm font-medium">Mevcut bakiye</div>
        <div className="font-display font-black text-white text-5xl md:text-6xl leading-tight mt-1">
          ₺{Number(user?.balance || 0).toFixed(2)}
        </div>
        <div className="flex flex-wrap gap-3 mt-5">
          <Link to="/deposit" data-testid="quick-deposit" className="inline-flex items-center gap-2 bg-white text-[hsl(217,91%,45%)] font-bold text-sm px-5 py-3 rounded-2xl hover:brightness-110 transition-all">
            <Plus className="w-4 h-4" /> Bakiye yükle
          </Link>
          <Link to="/numbers" data-testid="quick-numbers" className="inline-flex items-center gap-2 bg-white/15 backdrop-blur text-white font-bold text-sm px-5 py-3 rounded-2xl border border-white/25 hover:bg-white/20 transition-all">
            <Maximize2 className="w-4 h-4" /> Alınanlar
          </Link>
        </div>
      </div>

      {/* Step 1: Service */}
      <div className="border border-white/10 bg-[hsl(217,60%,10%)]/40 rounded-3xl p-5 md:p-6 mb-4">
        <StepHeader n={1} title="Servis seç" done={!!service} />
        <div className="relative mt-4 mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            data-testid="service-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Servis ara…"
            className="w-full bg-[hsl(0,0%,5%)] border border-white/10 focus:border-[hsl(217,91%,60%)] outline-none pl-11 pr-4 py-3 text-sm rounded-2xl"
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="services-grid">
          {filteredServices.map((s) => (
            <button
              key={s.code}
              onClick={() => setService(s.code)}
              data-selected={service === s.code}
              data-testid={`svc-${s.code}`}
              className="svc-tile rounded-2xl p-4 flex flex-col items-center justify-center gap-2 text-center"
            >
              <ServiceIcon code={s.code} size="md" />
              <div className="text-sm font-medium text-white leading-tight">{s.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Step 2: Country */}
      <div className="border border-white/10 bg-[hsl(217,60%,10%)]/40 rounded-3xl p-5 md:p-6 mb-4">
        <StepHeader n={2} title="Ülke seç" done={!!countryId} />
        <div className="relative mt-4 mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            data-testid="country-search"
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            placeholder="Ülke ara…"
            className="w-full bg-[hsl(0,0%,5%)] border border-white/10 focus:border-[hsl(217,91%,60%)] outline-none pl-11 pr-4 py-3 text-sm rounded-2xl"
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1" data-testid="countries-grid">
          {filteredCountries.map((c) => (
            <button
              key={c.id}
              onClick={() => setCountryId(c.id)}
              data-selected={countryId === c.id}
              data-testid={`ctry-${c.id}`}
              className="svc-tile rounded-xl p-3 text-left text-sm text-white flex items-center justify-between gap-2"
            >
              <span className="truncate">{c.country_name}</span>
              <span className="text-[10px] font-mono text-white/40">#{c.api_country_id}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Step 3: Price tiers */}
      {countryId && service && (
        <div className="border border-white/10 bg-[hsl(217,60%,10%)]/40 rounded-3xl p-5 md:p-6 mb-4">
          <StepHeader n={3} title="Fiyat kademesi seç" done={!!selectedTier} />
          <div className="text-xs text-white/50 mt-2 mb-4">
            {selectedService?.name} · {selectedCountry?.country_name} için mevcut fiyat seçenekleri:
          </div>
          {loadingTiers && (
            <div className="text-sm text-white/50">Fiyat kademeleri sorgulanıyor…</div>
          )}
          {!loadingTiers && tiers.length === 0 && (
            <div className="text-sm text-white/50 border border-dashed border-white/15 rounded-xl p-4">
              Bu servis ve ülke için şu an fiyat bilgisi yok.
            </div>
          )}
          {!loadingTiers && tiers.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tiers-grid">
              {tiers.map((t, i) => {
                const isSelected =
  selectedTier?.tier === t.tier &&
  selectedTier?.provider === t.provider &&
  selectedTier?.raw_cost === t.raw_cost;
                const isCheapest = i === 0;
                const isMostStock = tiers.every((x) => x.count <= t.count);
                return (
                  <button
                    key={`${t.provider}-${t.raw_cost}-${i}`}
                    onClick={() => setSelectedTier(t)}
                    data-selected={isSelected}
                    data-testid={`tier-${i}`}
                    className={`text-left p-4 rounded-2xl border transition-all ${
                      isSelected
                        ? "border-[hsl(43,74%,55%)] bg-[hsl(43,74%,45%)]/15 ring-2 ring-[hsl(43,74%,55%)]/40"
                        : "border-white/10 bg-[hsl(220,40%,9%)] hover:border-white/25"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(43,74%,65%)] font-bold" data-testid={`tier-slot-${i}`}>
                        {t.slot_label || `Sağlayıcı ${t.slot || 1}`}
                      </div>
                      <div className="flex gap-1">
                        {isCheapest && <Badge cls="text-[hsl(142,71%,55%)] border-[hsl(142,71%,55%)]/40">Ekonomik</Badge>}
                        {isMostStock && !isCheapest && <Badge cls="text-[hsl(43,74%,65%)] border-[hsl(43,74%,55%)]/40">Popüler</Badge>}
                      </div>
                    </div>
                    <div className="font-display font-black text-2xl text-white">₺{Number(t.final_price).toFixed(2)}</div>
                    <div className="text-xs text-white/50 mt-2 flex items-center gap-1.5">
                      <Package className="w-3 h-3" /> Stok: <span className="text-white/80 font-mono">{t.count}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Step 4: Confirm */}
      <div className="border border-white/10 bg-[hsl(217,60%,10%)]/40 rounded-3xl p-5 md:p-6">
        <StepHeader n={countryId && service ? 4 : 3} title="Onayla ve satın al" done={false} />
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <SummaryTile label="Servis" value={selectedService?.name} />
          <SummaryTile label="Ülke" value={selectedCountry?.country_name} />
          <SummaryTile label="Fiyat" value={selectedTier ? `₺${Number(selectedTier.final_price).toFixed(2)}` : "—"} big />
        </div>

        <button
          onClick={buy}
          disabled={!countryId || !service || !selectedTier || buying}
          data-testid="buy-submit-btn"
          className="btn-brand w-full font-bold text-base py-4 rounded-2xl inline-flex items-center justify-center gap-2 mt-5"
        >
          <Phone className="w-4 h-4" />
          {buying ? "Alınıyor…" : "Numarayı satın al"}
          <ChevronRight className="w-4 h-4" />
        </button>
        <div className="text-xs text-white/50 mt-3 leading-relaxed">
          Numarayı aldıktan sonra <b className="text-white">4. dakikadan önce</b> iptal edilemez. Kod gelmezse <b className="text-white">20. dakikada</b> tam iade otomatik yapılır.
        </div>
      </div>
    </UserLayout>
  );
}

function StepHeader({ n, title, done }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold text-sm font-mono ${
        done ? "bg-[hsl(142,71%,45%)] border-[hsl(142,71%,45%)] text-white" : "border-white/25 text-white/70"
      }`}>
        {n}
      </div>
      <div className="font-display font-bold text-lg">{title}</div>
    </div>
  );
}

function SummaryTile({ label, value, big }) {
  return (
    <div className="border border-white/10 rounded-2xl p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">{label}</div>
      <div className={`text-white font-bold ${big ? "font-display font-black text-xl" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Badge({ children, cls }) {
  return <span className={`border rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold ${cls}`}>{children}</span>;
}
